/**
 * Jodiso Authentication Handler
 * Global utility script - Include in theme.liquid
 * File: assets/auth-handler.js
 */

(function (window) {
  "use strict";

  // API Configuration
  const API_BASE_URL = "https://api.jodi-so.com";

  // Retry and Timeout Configuration
  const RETRY_CONFIG = {
    MAX_RETRIES: 3,
    TIMEOUT_MS: 9000, // 10 seconds
    INITIAL_RETRY_DELAY: 1000, // 1 second
    MAX_RETRY_DELAY: 30000, // 30 seconds
    // HTTP status codes that should trigger a retry
    RETRYABLE_STATUSES: [408, 500, 502, 503, 504],
  };

  /**
   * Fetch with timeout support
   */
  async function fetchWithTimeout(
    url,
    options = {},
    timeout = RETRY_CONFIG.TIMEOUT_MS
  ) {
    return Promise.race([
      fetch(url, options),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timeout")), timeout)
      ),
    ]);
  }

  /**
   * Calculate exponential backoff delay
   */
  function calculateRetryDelay(attemptNumber) {
    const delay = RETRY_CONFIG.INITIAL_RETRY_DELAY * Math.pow(2, attemptNumber);
    return Math.min(delay, RETRY_CONFIG.MAX_RETRY_DELAY);
  }

  /**
   * Check if status code is retryable
   */
  function isRetryableStatus(status) {
    return RETRY_CONFIG.RETRYABLE_STATUSES.includes(status);
  }

  /**
   * Check if error is retryable (network errors, timeouts)
   */
  function isRetryableError(error) {
    return (
      error.message === "Request timeout" ||
      error.message === "Failed to fetch" ||
      error.name === "TypeError"
    );
  }

  /**
   * Sleep/delay function
   */
  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Fetch with retry mechanism
   */
  async function retryableFetch(
    url,
    options = {},
    maxRetries = RETRY_CONFIG.MAX_RETRIES
  ) {
    let lastError;
    let lastStatus;
    let consecutiveIdenticalErrors = 0;
    let previousErrorMessage = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Attempt ${attempt + 1}/${maxRetries + 1} for ${url}`);

        const response = await fetchWithTimeout(url, options);
        lastStatus = response.status;

        // If response is OK or not retryable, return it
        if (response.ok || !isRetryableStatus(response.status)) {
          if (attempt > 0) {
            console.log(`✅ Request succeeded after ${attempt} retries`);
          }
          return response;
        }

        // Status is retryable but not OK
        console.warn(`⚠️ Retryable status ${response.status} received`);

        // Reset consecutive error counter since we got a response
        consecutiveIdenticalErrors = 0;
        previousErrorMessage = null;

        // If this is the last attempt, return the response as-is
        if (attempt === maxRetries) {
          console.error(`❌ Max retries (${maxRetries}) reached`);
          return response;
        }

        // Wait before retrying
        const delay = calculateRetryDelay(attempt);
        console.log(`⏳ Waiting ${delay}ms before retry...`);
        await sleep(delay);
      } catch (error) {
        lastError = error;
        console.error(`❌ Attempt ${attempt + 1} failed:`, error.message);

        // Check if error is retryable
        if (!isRetryableError(error)) {
          console.error(`❌ Non-retryable error encountered`);
          throw error;
        }

        // Check for repeated identical errors (likely CORS or config issue)
        if (error.message === previousErrorMessage) {
          consecutiveIdenticalErrors++;
        } else {
          consecutiveIdenticalErrors = 1;
          previousErrorMessage = error.message;
        }

        // Fail fast if we get the same error 2 times in a row
        if (consecutiveIdenticalErrors >= 2) {
          console.error(
            `❌ Same error repeated ${consecutiveIdenticalErrors} times - likely a configuration issue (CORS, network, etc.)`
          );
          throw error;
        }

        // If this is the last attempt, throw the error
        if (attempt === maxRetries) {
          console.error(`❌ Max retries (${maxRetries}) reached`);
          throw error;
        }

        // Wait before retrying
        const delay = calculateRetryDelay(attempt);
        console.log(`⏳ Waiting ${delay}ms before retry...`);
        await sleep(delay);
      }
    }

    // This shouldn't be reached, but just in case
    if (lastError) {
      throw lastError;
    }

    return new Response(
      JSON.stringify({
        error: "Max retries exceeded",
        status: lastStatus,
      }),
      {
        status: lastStatus || 500,
      }
    );
  }

  /**
   * Authentication State Management
   */
  const formatPhoneNumber = (numberString) => {
    if (numberString.startsWith("+962") && numberString.length == 13)
      return "0" + numberString.substring(4);
    if (numberString.startsWith("00962") && numberString.length == 14)
      return "0" + numberString.substring(5);
    if (numberString.startsWith("962") && numberString.length == 12)
      return "0" + numberString.substring(3);
    if (numberString.startsWith("7") && numberString.length == 9)
      return "0" + numberString;
    return numberString;
  };
  const AuthState = {
    // Check if user is authenticated
    isAuthenticated() {
      return !!localStorage.getItem("auth_token");
    },

    // Get stored user data
    getUser() {
      return {
        token: localStorage.getItem("auth_token"),
        customerId: localStorage.getItem("customer_id"),
        firstName: localStorage.getItem("customer_firstName"),
        lastName: localStorage.getItem("customer_lastName"),
        phone: localStorage.getItem("customer_phone"),
      };
    },

    // Store authentication data

    storeAuth(token, customer) {
      localStorage.setItem("auth_token", token);
      localStorage.setItem("customer_id", customer.id);
      localStorage.setItem("customer_firstName", customer.firstName);
      localStorage.setItem("customer_lastName", customer.lastName);
      localStorage.setItem("customer_phone", customer.phone);

      const storedAddress = localStorage.getItem("deliveryAddress");
      if (storedAddress) {
        let loadedAddress = JSON.parse(storedAddress);
        loadedAddress.name = customer.firstName + " " + customer.lastName;
        loadedAddress.phone = formatPhoneNumber(customer.phone);
        localStorage.setItem("deliveryAddress", JSON.stringify(loadedAddress));
      } else {
        localStorage.setItem(
          "deliveryAddress",
          JSON.stringify({
            name: customer.firstName + " " + customer.lastName,
            phone: formatPhoneNumber(customer.phone),
            city: "",
            area: "",
          })
        );
      }
      console.log("✅ Auth data stored:", {
        customerId: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
      });
    },

    // Clear authentication data
    clearAuth() {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("customer_id");
      localStorage.removeItem("customer_firstName");
      localStorage.removeItem("customer_lastName");
      localStorage.removeItem("customer_phone");
      localStorage.removeItem("deliveryAddress");
      localStorage.removeItem("paymentMethod");

      console.log("🔓 Auth data cleared");
    },
  };

  /**
   * API Methods
   */
  const API = {
    // Generate OTP
    async generateOTP(phone, firstName = null, lastName = null) {
      try {
        const response = await retryableFetch(
          `${API_BASE_URL}/api/otp/generate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ phone, firstName, lastName }),
          }
        );

        const data = await response.json();

        return {
          success: response.ok,
          data: data,
          status: response.status,
        };
      } catch (error) {
        console.error("❌ Generate OTP Error:", error);
        return {
          success: false,
          error: error.message,
          status: 0,
        };
      }
    },

    // Verify OTP
    async verifyOTP(phone, otp) {
      try {
        const response = await retryableFetch(
          `${API_BASE_URL}/api/otp/verify`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ phone, otp }),
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          // Store auth data
          AuthState.storeAuth(data.token, data.customer);
        }

        return {
          success: response.ok,
          data: data,
          nameRequired: data.nameRequired, // NEW: Signal if name input needed
          status: response.status,
        };
      } catch (error) {
        console.error("❌ Verify OTP Error:", error);
        return {
          success: false,
          error: error.message,
          status: 0,
        };
      }
    },

    // Update customer profile (firstName and lastName)
    async updateName(firstName, lastName) {
      try {
        const response = await retryableFetch(
          `${API_BASE_URL}/api/auth/update-name`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
            },
            body: JSON.stringify({ firstName, lastName }),
          }
        );

        const data = await response.json();

        // Handle 401 Unauthorized
        if (response.status === 401) {
          console.log("🔒 Unauthorized - Clearing auth and redirecting");
          AuthState.clearAuth();
          RedirectHandler.toLogin();
          throw new Error("Unauthorized");
        }

        if (response.ok && data.success) {
          // Update auth data with new token and profile
          AuthState.storeAuth(data.token, data.customer);
          console.log("✅ Profile updated and token refreshed");

          // Update UI to reflect new name
          UIHelpers.updateAuthUI();
        }

        return {
          success: response.ok,
          data: data,
          status: response.status,
        };
      } catch (error) {
        console.error("❌ Update Profile Error:", error);
        return {
          success: false,
          error: error.message,
          status: 0,
        };
      }
    },

    // Authenticated API call
    async authenticatedFetch(endpoint, options = {}) {
      const token = localStorage.getItem("auth_token");

      if (!token) {
        throw new Error("No authentication token found");
      }

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...options.headers,
      };

      try {
        const response = await retryableFetch(`${API_BASE_URL}${endpoint}`, {
          ...options,
          headers,
        });

        // Handle 401 Unauthorized (don't retry auth errors)
        if (response.status === 401) {
          console.log("🔒 Unauthorized - Clearing auth and redirecting");
          AuthState.clearAuth();
          RedirectHandler.toLogin();
          throw new Error("Unauthorized");
        }

        const data = await response.json();

        return {
          success: response.ok,
          data: data,
          status: response.status,
        };
      } catch (error) {
        console.error("❌ Authenticated Fetch Error:", error);
        throw error;
      }
    },

    // Get customer orders
    async getOrders() {
      return this.authenticatedFetch("/api/auth/orders", {
        method: "GET",
      });
    },

    // Create manual payment order
    async createManualPaymentOrder(payload) {
      return this.authenticatedFetch("/api/auth/create-order/manual-payment", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    // Create online payment order
    async createOnlinePaymentOrder(payload) {
      return this.authenticatedFetch("/api/auth/create-order/online-payment", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    // Revoke all sessions (sign out all devices)
    async revokeSessions() {
      try {
        const response = await retryableFetch(
          `${API_BASE_URL}/api/auth/revoke-sessions`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
            },
          }
        );

        const data = await response.json();

        // If successful, immediately clear auth and redirect
        if (response.ok && data.success) {
          console.log("🔒 All sessions revoked - logging out");
          AuthState.clearAuth();
          RedirectHandler.toLogin();
        }

        return {
          success: response.ok,
          data: data,
          status: response.status,
        };
      } catch (error) {
        console.error("❌ Revoke Sessions Error:", error);
        return {
          success: false,
          error: error.message,
          status: 0,
        };
      }
    },
  };

  /**
   * Redirect Handler
   */
  const RedirectHandler = {
    // Redirect to login page
    toLogin() {
      if (!window.location.pathname.includes("/pages/login")) {
        window.location.href = "/pages/login";
      }
    },

    // Redirect to profile page
    toProfile() {
      window.location.href = "/pages/profile";
    },

    // Handle post-verification redirect
    handlePostVerification() {
      const currentPath = window.location.pathname;

      if (currentPath.includes("/pages/login")) {
        // From login page → Go to profile
        console.log("✅ Redirecting to profile...");
        this.toProfile();
      } else if (currentPath.includes("/cart")) {
        // From cart → Stay, just show success
        console.log("✅ Authenticated on cart - No redirect");
        return "stay";
      } else {
        // Other pages → Reload
        console.log("✅ Authenticated - Reloading page");
        window.location.reload();
      }
    },
  };

  /**
   * UI Helpers
   */
  const UIHelpers = {
    // Show loading state on button
    setButtonLoading(button, isLoading, originalText) {
      if (isLoading) {
        button.disabled = true;
        button.dataset.originalText = button.textContent;
        button.textContent = "⏳ جاري التحميل...";
      } else {
        button.disabled = false;
        button.textContent =
          originalText || button.dataset.originalText || button.textContent;
      }
    },

    // Show message in element
    showMessage(element, message, type = "info") {
      if (!element) return;

      element.textContent = message;
      element.style.display = "block";

      // Set colors based on type
      const styles = {
        success: { bg: "#d4edda", color: "#155724", border: "#c3e6cb" },
        error: { bg: "#f8d7da", color: "#721c24", border: "#f5c6cb" },
        warning: { bg: "#fff3cd", color: "#856404", border: "#ffeaa7" },
        info: { bg: "#d1ecf1", color: "#0c5460", border: "#bee5eb" },
      };

      const style = styles[type] || styles.info;
      element.style.backgroundColor = style.bg;
      element.style.color = style.color;
      element.style.border = `1px solid ${style.border}`;
      element.style.padding = "12px";
      element.style.borderRadius = "4px";
      element.style.marginTop = "15px";
    },

    // Hide message
    hideMessage(element) {
      if (!element) return;
      element.style.display = "none";
      element.textContent = "";
    },

    // Update UI based on auth state
    updateAuthUI() {
      const isAuth = AuthState.isAuthenticated();
      const user = AuthState.getUser();

      // Update elements with data-auth-show attribute
      document.querySelectorAll("[data-auth-show]").forEach((el) => {
        const show = el.dataset.authShow === "true";
        el.style.display = isAuth === show ? "" : "none";
      });

      // Update elements with data-auth-hide attribute
      document.querySelectorAll("[data-auth-hide]").forEach((el) => {
        const hide = el.dataset.authHide === "true";
        el.style.display = isAuth === hide ? "none" : "";
      });

      // Update customer name elements
      document.querySelectorAll("[data-customer-name]").forEach((el) => {
        const fullName =
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : "العميل";
        el.textContent = fullName;
      });

      // Update customer firstName elements
      document.querySelectorAll("[data-customer-firstName]").forEach((el) => {
        el.textContent = user.firstName || "";
      });

      // Update customer lastName elements
      document.querySelectorAll("[data-customer-lastName]").forEach((el) => {
        el.textContent = user.lastName || "";
      });

      // Update customer phone elements
      document.querySelectorAll("[data-customer-phone]").forEach((el) => {
        el.textContent = user.phone || "";
      });
    },
  };

  /**
   * Logout Handler
   */
  function logout() {
    AuthState.clearAuth();
    RedirectHandler.toLogin();
  }

  /**
   * Initialize on page load
   */
  function init() {
    console.log("🔐 Jodiso Auth Handler Initialized");

    // Update UI based on auth state
    UIHelpers.updateAuthUI();

    // Check if on protected page without auth
    const protectedPages = ["/pages/profile", "/account"];
    const currentPath = window.location.pathname;

    if (protectedPages.some((page) => currentPath.includes(page))) {
      if (!AuthState.isAuthenticated()) {
        console.log("⚠️ Protected page - Redirecting to login");
        RedirectHandler.toLogin();
      }
    }
  }

  // Auto-initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /**
   * Public API - Expose to window
   */
  window.JodisoAuth = {
    // State management
    isAuthenticated: AuthState.isAuthenticated.bind(AuthState),
    getUser: AuthState.getUser.bind(AuthState),
    logout: logout,

    // API methods
    generateOTP: API.generateOTP.bind(API),
    verifyOTP: API.verifyOTP.bind(API),
    updateName: API.updateName.bind(API),
    getOrders: API.getOrders.bind(API),
    createManualPaymentOrder: API.createManualPaymentOrder.bind(API),
    createOnlinePaymentOrder: API.createOnlinePaymentOrder.bind(API),
    revokeSessions: API.revokeSessions.bind(API),
    authenticatedFetch: API.authenticatedFetch.bind(API),

    // Redirect
    redirectToLogin: RedirectHandler.toLogin.bind(RedirectHandler),
    redirectToProfile: RedirectHandler.toProfile.bind(RedirectHandler),
    handlePostVerification:
      RedirectHandler.handlePostVerification.bind(RedirectHandler),

    // UI Helpers
    setButtonLoading: UIHelpers.setButtonLoading.bind(UIHelpers),
    showMessage: UIHelpers.showMessage.bind(UIHelpers),
    hideMessage: UIHelpers.hideMessage.bind(UIHelpers),
    updateAuthUI: UIHelpers.updateAuthUI.bind(UIHelpers),
  };
})(window);
